/**
 * HostPoint POS - Device Registration Service
 * Handles device registration checks and POS access control
 */

const DEVICE_ID_KEY = "hostpoint_device_id";
const DEVICE_REGISTRATION_KEY = "hostpoint_device_registration";
const BROWSER_FINGERPRINT_KEY = "hostpoint_browser_fingerprint";
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generate a robust browser fingerprint that persists across cache clears.
 * Uses multiple browser attributes to create a semi-unique identifier.
 * This fingerprint is stored in IndexedDB for persistence.
 */
export async function generateBrowserFingerprint() {
  const components = [];
  
  // 1. Canvas fingerprint (most unique)
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("HostPoint POS FP", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("HostPoint POS FP", 4, 17);
    components.push(canvas.toDataURL());
  } catch (e) {
    components.push("canvas-error");
  }
  
  // 2. WebGL fingerprint
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl) {
      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
    }
  } catch (e) {
    components.push("webgl-error");
  }
  
  // 3. Screen properties
  components.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);
  components.push(screen.pixelDepth);
  components.push(window.devicePixelRatio || 1);
  
  // 4. Timezone
  components.push(new Date().getTimezoneOffset());
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  
  // 5. Language and platform
  components.push(navigator.language);
  components.push(navigator.languages?.join(",") || "");
  components.push(navigator.platform);
  
  // 6. Hardware concurrency
  components.push(navigator.hardwareConcurrency || "unknown");
  
  // 7. Touch support
  components.push(navigator.maxTouchPoints || 0);
  
  // 8. Audio context fingerprint
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const analyser = audioContext.createAnalyser();
    const gainNode = audioContext.createGain();
    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
    
    gainNode.gain.value = 0;
    oscillator.type = "triangle";
    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.start(0);
    components.push(audioContext.sampleRate);
    oscillator.stop();
    audioContext.close();
  } catch (e) {
    components.push("audio-error");
  }
  
  // Create hash from all components
  const fingerprint = components.join("|||");
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  // Create a longer, more unique hash
  const hashStr = Math.abs(hash).toString(16).toUpperCase().padStart(8, "0");
  const secondHash = Math.abs(hash * 31).toString(16).toUpperCase().padStart(8, "0");
  
  return `FP-${hashStr}-${secondHash}`;
}

/**
 * Get or generate browser fingerprint (cached in memory and IndexedDB)
 */
let cachedFingerprint = null;

export async function getBrowserFingerprint() {
  if (cachedFingerprint) {
    return cachedFingerprint;
  }
  
  // Try to get from IndexedDB first (persists across cache clears)
  try {
    const stored = await getFromIndexedDB(BROWSER_FINGERPRINT_KEY);
    if (stored) {
      cachedFingerprint = stored;
      return stored;
    }
  } catch (e) {
  }
  
  // Generate new fingerprint
  cachedFingerprint = await generateBrowserFingerprint();
  
  // Store in IndexedDB for persistence
  try {
    await saveToIndexedDB(BROWSER_FINGERPRINT_KEY, cachedFingerprint);
  } catch (e) {
  }
  
  return cachedFingerprint;
}

/**
 * IndexedDB helpers for fingerprint persistence
 */
const DB_NAME = "hostpoint_device_db";
const STORE_NAME = "device_data";

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
  });
}

async function saveToIndexedDB(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.put({ key, value, timestamp: Date.now() });
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
    tx.oncomplete = () => db.close();
  });
}

async function getFromIndexedDB(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result?.value);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Generate or retrieve a unique device ID using browser fingerprinting
 */
export function getDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    // Generate a fingerprint based on available browser/device info
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillText("HostPoint POS", 2, 2);
    const canvasData = canvas.toDataURL();
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + "x" + screen.height,
      new Date().getTimezoneOffset(),
      canvasData.slice(-50)
    ].join("|");
    
    // Create a simple hash
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    deviceId = "DEV-" + Math.abs(hash).toString(16).toUpperCase().padStart(8, "0") + 
               "-" + Date.now().toString(36).toUpperCase();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Get cached device registration status
 */
export function getCachedRegistration() {
  const cached = localStorage.getItem(DEVICE_REGISTRATION_KEY);
  if (cached) {
    try {
      const data = JSON.parse(cached);
      // Cache expires after 5 minutes
      if (data.timestamp && Date.now() - data.timestamp < 5 * 60 * 1000) {
        return data;
      }
    } catch (e) {
      // Invalid cache
    }
  }
  return null;
}

/**
 * Cache device registration status
 */
export function cacheRegistration(data) {
  localStorage.setItem(DEVICE_REGISTRATION_KEY, JSON.stringify({
    ...data,
    timestamp: Date.now()
  }));
}

/**
 * Clear cached registration
 */
export function clearCachedRegistration() {
  localStorage.removeItem(DEVICE_REGISTRATION_KEY);
}

/**
 * Check if device is registered (async API call)
 * @returns {Promise<{registered: boolean, active: boolean, tenant_name?: string, property_code?: string, outlets?: array}>}
 */
export async function checkDeviceRegistration() {
  const deviceId = getDeviceId();
  
  try {
    const response = await fetch(`${API_URL}/mobile-devices/check/${deviceId}`);
    const data = await response.json();
    
    // Cache the result
    cacheRegistration(data);
    
    return data;
  } catch (error) {
    // Return cached data if available
    const cached = getCachedRegistration();
    if (cached) {
      return cached;
    }
    // Return unregistered status on error
    return { registered: false, message: "Could not verify device registration" };
  }
}

/**
 * Check if device is registered and active (quick check using cache)
 * @returns {{registered: boolean, active: boolean, data: object|null}}
 */
export function isDeviceRegistered() {
  const cached = getCachedRegistration();
  if (cached) {
    return {
      registered: cached.registered === true,
      active: cached.active === true,
      data: cached
    };
  }
  return { registered: false, active: false, data: null };
}

/**
 * Check if this device should use PIN login as default
 * Registered devices should always use PIN login
 */
export function shouldUsePinLogin() {
  const { registered, active } = isDeviceRegistered();
  return registered && active;
}

/**
 * Get the appropriate login redirect URL based on device registration
 */
export function getLoginRedirectUrl() {
  if (shouldUsePinLogin()) {
    return "/pin-login";
  }
  return "/login";
}

/**
 * Sync device registration status (call on app init or after login)
 */
export async function syncDeviceRegistration() {
  const result = await checkDeviceRegistration();
  return result;
}

/**
 * Look up device by browser fingerprint when localStorage is cleared
 * Returns device info if a match is found
 */
export async function lookupDeviceByFingerprint() {
  try {
    const fingerprint = await getBrowserFingerprint();
    
    const response = await fetch(`${API_URL}/mobile-devices/lookup-by-fingerprint?fingerprint=${encodeURIComponent(fingerprint)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    return { found: false, message: "Lookup failed" };
  }
}

/**
 * Restore device registration after user confirms fingerprint recognition
 */
export async function restoreDeviceByFingerprint(deviceId) {
  try {
    const fingerprint = await getBrowserFingerprint();
    
    const response = await fetch(`${API_URL}/mobile-devices/restore-by-fingerprint?fingerprint=${encodeURIComponent(fingerprint)}&device_id=${encodeURIComponent(deviceId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Restore failed");
    }
    
    const data = await response.json();
    
    if (data.success) {
      // Restore device_id to localStorage
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
      // Cache the registration data
      cacheRegistration(data);
    }
    
    return data;
  } catch (error) {
    return { success: false, message: error.message || "Restore failed" };
  }
}

export default {
  getDeviceId,
  getBrowserFingerprint,
  generateBrowserFingerprint,
  checkDeviceRegistration,
  isDeviceRegistered,
  shouldUsePinLogin,
  getLoginRedirectUrl,
  syncDeviceRegistration,
  getCachedRegistration,
  cacheRegistration,
  clearCachedRegistration,
  lookupDeviceByFingerprint,
  restoreDeviceByFingerprint
};
